import * as React from 'react';

import { Box } from '../../components/ui/box';
import { Text } from '../../components/ui/text';
import type { GridColumns } from './grid-columns';
import { GRID_LAYER } from './grid-layers-z';
import type { GridLayoutItem, GridRect } from './grid-layout';
import type { Omittable } from "../../lib/props";

// The grid's decoration layers — aria-hidden, pointer-inert leaves the shell
// composes into the grid body (cell overlay, resize ghost, target outline,
// external phantom, marquee selection rect, gesture floor spacer). Pure
// presentation over explicit props; no gesture state, no context.

// The live `w×h` span readout pinned inside the target outline (and the
// external drop phantom). An overlay-tier elevated chip so it reads above any
// tone the tiles below it wear.
export function SpanBadge({ w, h }: { w: number; h: number }) {
  return (
    <Box
      surface="popover"
      render={
        <Box pointerEvents="none" position="absolute" rounded="md" paddingY="2xs" className="top-1 right-1 border px-1.5" />
      }
    >
      <Text size="xs" weight="medium" variant="muted">
        {`${w} × ${h}`}
      </Text>
    </Box>
  );
}

// THE grid guide: a dot at every gap-center intersection between cells. ONE
// element painting a repeating CSS background — no per-cell divs — covering
// the grid's full height so the whole buildable area reads, not just the used
// rows. Permanently mounted with an opacity fade: build surfaces hold it
// visible (`guides`), everywhere else it reveals only while a gesture runs.
// Colors ride tokens via color-mix (never `/N` on a var). Dot alpha (22%) is
// DESIGN-TUNABLE, here only.
export function GridGuideLayer({
  visible,
  columns,
  gapPx,
  rowHeight,
}: {
  visible: boolean;
  columns: GridColumns;
  gapPx: number;
  rowHeight: number;
}) {
  return (
    <Box pointerEvents="none" fill="absolute" transition="opacity" duration={150}
      className="ease-out"
      style={{
        opacity: visible ? 1 : 0,
        backgroundImage:
          'radial-gradient(circle, color-mix(in oklab, var(--foreground) 22%, transparent) 1px, transparent 1.5px)',
        // Horizontal pitch: track + gap = (100% + gap) / columns.
        backgroundSize: `calc((100% + ${gapPx}px) / ${columns}) ${rowHeight + gapPx}px`,
        backgroundPosition: `${-gapPx / 2}px ${-gapPx / 2}px`,
        zIndex: GRID_LAYER.guides,
      }}
      aria-hidden
    />
  );
}

/** Invisible grid-placed spacer pinning implicit rows through the gesture row
 *  floor: the grid can GROW mid-gesture (previews beyond the floor create
 *  more implicit rows) but never shrink — kills mid-gesture scroll jumps.
 *  Renders nothing when no floor is pinned. */
export function GridFloorSpacer({ rows }: { rows: number | null }) {
  if (rows == null || rows <= 0) return null;
  return (
    <Box pointerEvents="none"
      aria-hidden
      style={{
        gridColumnStart: 1,
        gridRowStart: rows,
        gridRowEnd: 'span 1',
      }}
    />
  );
}

/** The resize ghost. Geometry (left/top/width/height) is set imperatively by
 *  the resize gesture through `ghostRef` — kept out of the style object so a
 *  reflow re-render never clobbers the live values; only the opacity is
 *  render-driven. */
export function GridResizeGhost({
  ghostRef,
  active,
}: {
  ghostRef: (node: HTMLElement | null) => void;
  active: boolean;
}) {
  return (
    <Box pointerEvents="none" position="absolute" rounded="xl" transition="opacity" duration={150}
      ref={ghostRef}
      aria-hidden
      className="bg-foreground/[0.06] ease-out"
      style={{
        opacity: active ? 1 : 0,
        zIndex: GRID_LAYER.decoration,
      }}
    />
  );
}

/** Target outline + span badge for the tile being dragged/resized: its
 *  previewed rect IS its landing cell. */
export function GridTargetOutline({ rect }: { rect: GridLayoutItem }) {
  return (
    <Box pointerEvents="none" position="relative" rounded="xl" borderWidth={2} borderStyle="dashed"
      aria-hidden
      className="border-primary/40"
      style={{
        gridColumnStart: rect.x + 1,
        gridColumnEnd: `span ${rect.w}`,
        gridRowStart: rect.y + 1,
        gridRowEnd: `span ${rect.h}`,
        zIndex: GRID_LAYER.decoration,
      }}
    >
      <SpanBadge w={rect.w} h={rect.h} />
    </Box>
  );
}

/** The dashed phantom marking where an external (palette/foreign-tile) drop
 *  will land. */
export function GridExternalPhantom({ rect }: { rect: GridLayoutItem }) {
  return (
    <Box pointerEvents="none" position="relative" rounded="xl" borderWidth={2} borderStyle="dashed"
      aria-hidden
      className="border-foreground/20 bg-foreground/[0.06]"
      style={{
        gridColumnStart: rect.x + 1,
        gridColumnEnd: `span ${rect.w}`,
        gridRowStart: rect.y + 1,
        gridRowEnd: `span ${rect.h}`,
        zIndex: GRID_LAYER.decoration,
      }}
    >
      <SpanBadge w={rect.w} h={rect.h} />
    </Box>
  );
}

/** Marquee selection rect (live while sweeping, settled while the picker is
 *  open). A grid-placed child, so a below-content selection keeps its
 *  implicit rows after the floor drops — the picker anchor can't drift.
 *  The guide dots inside the rect re-paint in the theme color: the same
 *  lattice as `GridGuideLayer`, with `backgroundOrigin: border-box` so the
 *  pattern anchors on the cell box (the 2px border would otherwise shift it)
 *  and the local pitch `(100% + gap) / w` lands each dot exactly on its
 *  page-layer twin. Hit model: the rect and the centered anchor wrapper are
 *  `pointer-events-none` (clicks fall through to the grid), while the picker
 *  content itself re-enables `pointer-events-auto`. */
export function GridSelectionRect({
  selection,
  settled,
  gapPx,
  rowHeight,
  renderAreaSelection,
}: Omittable<{
  selection: GridRect;
  settled: GridRect | null;
  gapPx: number;
  rowHeight: number;
  renderAreaSelection?: (area: GridRect) => React.ReactNode;
}>) {
  return (
    <Box pointerEvents="none" position="relative" rounded="xl" borderWidth={2} borderStyle="dashed"
      className="border-primary/40 bg-primary/[0.04]"
      style={{
        gridColumnStart: selection.x + 1,
        gridColumnEnd: `span ${selection.w}`,
        gridRowStart: selection.y + 1,
        gridRowEnd: `span ${selection.h}`,
        zIndex: GRID_LAYER.selection,
        backgroundImage:
          'radial-gradient(circle, var(--primary) 1px, transparent 1.5px)',
        backgroundSize: `calc((100% + ${gapPx}px) / ${selection.w}) ${rowHeight + gapPx}px`,
        backgroundPosition: `${-gapPx / 2}px ${-gapPx / 2}px`,
        backgroundOrigin: 'border-box',
      }}
    >
      <SpanBadge w={selection.w} h={selection.h} />
      {settled && renderAreaSelection ? (
        <Box pointerEvents="none" fill="absolute" align="center" justify="center">
          <Box pointerEvents="auto">
            {renderAreaSelection(settled)}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
